/**
 * Hardens cookie options for session / auth cookies set via the
 * `@supabase/ssr` server clients.
 *
 * Aikido security scan #29730630 (issue #585) flagged that the Supabase
 * session cookie (`sb-*-auth-token`) at `https://uat.solution.co.uk/admin`
 * was missing the `httpOnly` and `secure` attributes, allowing 3rd-party
 * scripts to read the session token. The follow-up DAST re-scan that
 * produced issue #574 re-flagged the same cookie for `Secure` because
 * the previous gate (`NODE_ENV === 'production'`) does not match the
 * UAT runtime environment — UAT runs over HTTPS but does not set
 * `NODE_ENV=production`, so the cookie was emitted plain.
 *
 * Defaults enforced on every server-side cookie write:
 *   - `httpOnly: true`  — blocks `document.cookie` access from JS
 *   - `secure: true`    — only sent over HTTPS. Set in every
 *                         environment except local `development`,
 *                         where the dev server is plain HTTP.
 *   - `sameSite: 'lax'` — sensible default; overridable via input options
 *
 * Exception — browser-readable Supabase cookies:
 *   `lib/supabase/client.ts` uses `createBrowserClient` from
 *   `@supabase/ssr` to store the session in cookies (not localStorage).
 *   The browser-side SDK reads the access token from `document.cookie`
 *   on every `supabase.auth.*` call. Setting `httpOnly: true` on the
 *   `sb-<ref>-auth-token` cookie (and its chunked / code-verifier
 *   siblings) hides them from JS and breaks every client-side auth
 *   call — see the change-request on !544 for a captured reproduction
 *   of MFA failure caused by exactly this.
 *
 *   For these cookies we still enforce `secure` and `sameSite=lax`
 *   (so the token cannot be exfiltrated over plaintext and cannot be
 *   sent on cross-site sub-requests). HttpOnly compensation for them
 *   is delivered by the strict CSP from !536 (no inline scripts from
 *   untrusted origins). Aikido issue #583 re-flags the missing
 *   `HttpOnly` on `sb-api-uat-auth-token`; the decision above stands
 *   — risk-accepted with the CSP as compensating control.
 *
 * Pass-through preserves any other attributes set by `@supabase/ssr`
 * (e.g. `path`, `maxAge`, `domain`).
 */

import type { CookieOptions } from '@supabase/ssr';

/**
 * Whether server-set cookies should carry the `Secure` attribute.
 *
 * Resolved per call (not captured at module load) so consumers and
 * tests can flip `NODE_ENV` without needing to reset module caches.
 *
 * Returns `true` in every runtime except local `development`. Any
 * non-dev environment (`production`, `test`, `staging`, `uat`,
 * an empty / unset value, etc.) is treated as HTTPS-capable.
 *
 * Escape hatch — `E2E_INSECURE_COOKIES=1`: the production-build E2E
 * container (`app-e2e`, `NODE_ENV=production`) is served over plain
 * HTTP (`http://localhost:3000`). WebKit silently drops `Secure`
 * cookies received over HTTP — unlike Chromium/Firefox it has no
 * `localhost` secure-context exception — so the `sb-*-auth-token`
 * session cookie never lands and client-side `supabase.auth.*` calls
 * fail with "Auth session missing!". This flag, set only on `app-e2e`,
 * drops `Secure` so the HTTP transport works; it is never set in real
 * `production`/`uat`/`staging`, which keep the Aikido `#574`/`#585`
 * `Secure` guarantee intact.
 */
export function cookieSecureDefault(): boolean {
  if (process.env.E2E_INSECURE_COOKIES === '1') return false;
  return process.env.NODE_ENV !== 'development';
}

/**
 * Whether cookies written by the *browser* SDK (`document.cookie` via
 * `createBrowserClient`) should carry the `Secure` attribute.
 *
 * Aikido issues #348 (`https://uat.solution.co.uk/admin`) and #349
 * (`https://api-uat.solution.co.uk`) re-flagged the session cookie as
 * unsecured. `hardenCookieOptions` only covers *server* writes; when a
 * user signs in client-side, `@supabase/ssr` writes the same
 * `sb-<ref>-auth-token` cookie from JS using its own
 * `DEFAULT_COOKIE_OPTIONS`, which has no `secure` flag — so on UAT the
 * cookie was emitted without `Secure`.
 *
 * `NODE_ENV` is not a usable gate here: it is inlined into the client
 * bundle at build time, and `E2E_INSECURE_COOKIES` is server-only (not
 * `NEXT_PUBLIC_`), so a production build served over plain HTTP (the
 * `app-e2e` container) would stamp `Secure` and WebKit would silently
 * drop the cookie. The page protocol is the accurate signal: HTTPS
 * origins (UAT, staging, production) get `Secure`, plaintext local /
 * E2E origins do not.
 *
 * Returns `false` outside the browser — server-side writes go through
 * `hardenCookieOptions` instead.
 */
export function browserCookieSecureDefault(): boolean {
  if (typeof window === 'undefined') return false;
  return window.location?.protocol === 'https:';
}

/**
 * Matches every cookie name the browser-side `@supabase/ssr` client
 * reads via `document.cookie`:
 *   - `sb-<ref>-auth-token`            (base cookie)
 *   - `sb-<ref>-auth-token.0` / `.1`   (chunked when token > ~3.6KB)
 *   - `sb-<ref>-auth-token-code-verifier` (PKCE flow)
 *
 * No end anchor — any descendant of the auth-token namespace is treated
 * as browser-readable.
 */
const BROWSER_READABLE_SUPABASE_COOKIE = /^sb-.+-auth-token/;

export function hardenCookieOptions(
  options: CookieOptions = {},
  name?: string,
): CookieOptions {
  const isBrowserReadable =
    !!name && BROWSER_READABLE_SUPABASE_COOKIE.test(name);
  return {
    ...options,
    httpOnly: isBrowserReadable ? options.httpOnly ?? false : true,
    secure: cookieSecureDefault(),
    sameSite: options.sameSite ?? 'lax',
  };
}
